'use client';

import Link from 'next/link';

import { formatDayLong } from '@/lib/date';
import { Button, Spinner } from '@holistic/components';
import {
  CreditCard as CreditCardIcon,
  Lock,
  Receipt,
  ShieldCheck,
  Truck,
} from '@holistic/components/icons';

import type { CargoAddress } from '@/types/cargo';
import type { TalepRequestType } from '../types';
import type { PaymentFormValues } from './payment-schema';

import PriceBreakdown from '../price-breakdown';

const TRUST_ITEMS = [
  { icon: Truck, label: 'Ücretsiz kargo' },
  { icon: Receipt, label: 'Şeffaf fiyat' },
  { icon: ShieldCheck, label: '6 ay garanti' },
];

type PaymentCardProps = {
  address: CargoAddress;
  pickupDate: string | null;
  /** Seçilen arızalar; ödeme özetindeki kalem dökümü buradan çıkar. */
  requestTypes: TalepRequestType[];
  /** Garanti kapsamındaki talepte ücret alınmaz; kart formu da gösterilmez. */
  isWarrantyCovered: boolean;
  /** Adres/randevu doğrulaması; false dönerse gönderim yapılmaz. */
  validate: () => boolean;
  /**
   * Kart bilgileriyle çağrılırsa 3D Secure ödemesi başlatılır; `null` ile
   * çağrılırsa (garanti / inceleme sonrası fiyatlanacak talep) tahsilat
   * yapılmadan talep açılır.
   */
  onSubmit: (card: PaymentFormValues | null) => void;
  isSubmitting: boolean;
};

/**
 * Ödeme kutusu.
 *
 * İki farklı gönderim var:
 *  - Tahsilat gerekiyorsa kart doğrulanır ve `POST /payment/initiate` ile Tami
 *    3D Secure akışı başlar; talep, kargo çağrısı ve ödeme kaydı ancak banka
 *    onayından sonra (Tami callback'inde) backend'de oluşur.
 *  - Garanti kapsamındaki ya da tutarı inceleme sonrası netleşecek taleplerde
 *    tahsilat yoktur; `POST /repair-request/add` ile talep doğrudan açılır.
 *
 * Kart bilgileri backend'de saklanmaz, yalnızca bankaya iletilir.
 */
const PaymentCard = ({
  address,
  pickupDate,
  requestTypes,
  isWarrantyCovered,
  validate,
  onSubmit,
  isSubmitting,
}: PaymentCardProps) => {
  const location = [address.town?.name, address.city?.name]
    .filter(Boolean)
    .join(' / ');

  const handleClick = () => {
    if (validate()) onSubmit(null);
  };

  return (
    <div className="flex flex-col gap-5">
      {isWarrantyCovered ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm leading-relaxed text-emerald-800">
          <p className="font-medium">Garanti Kapsamında Talep</p>
          <p className="mt-0.5 text-xs text-emerald-700">
            Talep garanti kapsamında olduğu için herhangi bir onarım ücreti
            alınmayacaktır.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/70 p-4 text-sm leading-relaxed text-amber-900">
          <div className="flex items-center gap-2 font-medium text-amber-900">
            <CreditCardIcon className="size-4 text-amber-700" />
            <span>Ön Ödeme Alınmamaktadır</span>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-amber-800/90">
            Cihazınız servisimize ulaşıp uzman teknisyenlerimiz tarafından
            incelendikten sonra SMS veya e-posta ile tarafınıza iletilen
            bilgilendirme üzerinden güvenli 3D Secure ile ödemenizi
            tamamlayabilirsiniz. Cihaz onarımı ödemeniz ve onayınız sonrasında
            başlatılacaktır.
          </p>
        </div>
      )}

      <ul className="flex flex-col gap-2.5 border-t border-gray-100 pt-4">
        <SummaryLine
          label="Teslim alma"
          value={location || 'Adres seçilmedi'}
        />
        <SummaryLine
          label="Kurye günü"
          value={pickupDate ? formatDayLong(pickupDate) : 'Seçilmedi'}
        />
      </ul>

      <div className="border-t border-gray-100 pt-4">
        <PriceBreakdown
          items={requestTypes}
          isWarrantyCovered={isWarrantyCovered}
        />
      </div>

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          onClick={handleClick}
          disabled={isSubmitting}
          className="shadow-primary/25 h-11 w-full rounded-full text-sm font-semibold shadow-md"
        >
          {isSubmitting ? (
            <>
              <Spinner className="mr-2 size-4" />
              Talebiniz Alınıyor...
            </>
          ) : (
            <>
              Talebi Onayla ve Gönder
              <ShieldCheck className="ml-1.5 size-4" />
            </>
          )}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
          <Lock className="size-3" />
          Önceden herhangi bir kart bilgisi istenmez ve tahsilat yapılmaz.
        </p>

        {/* Mesafeli satışta sözleşmelerin sipariş öncesi erişilebilir olması
            gerekiyor; yeni sekmede açılır ki doldurulan form kaybolmasın. */}
        <p className="text-center text-[11px] leading-relaxed text-gray-400">
          Talebi göndererek{' '}
          <Link
            href="/sozlesmeler/mesafeli-satis-sozlesmesi"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            Mesafeli Satış Sözleşmesi
          </Link>
          ,{' '}
          <Link
            href="/sozlesmeler/garanti-kosullari"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            Garanti Koşulları
          </Link>{' '}
          ve{' '}
          <Link
            href="/sozlesmeler/aydinlatma-metni"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            Aydınlatma Metni
          </Link>
          ’ni kabul etmiş olursunuz.
        </p>
      </div>

      <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-gray-100 pt-4">
        {TRUST_ITEMS.map(({ icon: Icon, label }) => (
          <li
            key={label}
            className="flex items-center gap-1.5 text-[11px] text-gray-400"
          >
            <Icon className="text-primary/70 size-3.5" />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SummaryLine = ({ label, value }: { label: string; value: string }) => (
  <li className="flex items-baseline justify-between gap-4">
    <span className="shrink-0 text-[13px] text-gray-400">{label}</span>
    <span className="truncate text-[13px] font-medium text-gray-800">
      {value}
    </span>
  </li>
);

export default PaymentCard;
